import type { OrderStatus } from '@/types';
import { ORDER_STATUSES } from '@/types';
import type { ActivityEvent, CustomerProfile } from '@/types/admin';
import { canTransition, TRANSITION_NEEDS } from '@/lib/orderStatus';
import { titleCase } from '@/lib/format';
import { ServiceError } from '../../contracts';
import type { AdminCustomerService, AdminOrderService } from '../../adminContracts';
import { anchorTime, ctx, customerBalance, DAY_MS, delay, hydrateCustomers, matches, nowIso, paginate, saveAdmin, uid } from './context';

const notFound = (w: string) => new ServiceError(`${w} not found`, 'NOT_FOUND');

export const adminOrders: AdminOrderService = {
  async list(q) {
    const { data: d } = ctx('orders.read', 'orders');
    const from = q.from ? Date.parse(q.from) : undefined;
    const to = q.to ? Date.parse(q.to) + DAY_MS - 1 : undefined; // inclusive end date
    const rows = d.orders
      .filter((o) =>
        (!q.status || o.status === q.status) && (!q.paymentStatus || o.paymentStatus === q.paymentStatus) && (!q.customerId || o.customer.id === q.customerId)
        && (from == null || Date.parse(o.placedAt) >= from) && (to == null || Date.parse(o.placedAt) <= to)
        && matches(q.q, o.number, o.customer.name, o.customer.email))
      .sort((a, b) => b.placedAt.localeCompare(a.placedAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async get(id) {
    const { data: d } = ctx('orders.read', 'orders');
    const o = d.orders.find((x) => x.id === id || x.number === id);
    if (!o) throw notFound('Order');
    return delay(o);
  },
  async transition(id, input) {
    const { actor, data: d } = ctx('orders.manage', 'orders');
    const o = d.orders.find((x) => x.id === id);
    if (!o) throw notFound('Order');
    if (!canTransition(o.status, input.to)) {
      throw new ServiceError(`An order that is ${titleCase(o.status)} cannot be moved to ${titleCase(input.to)}.`, 'CONFLICT');
    }
    const need = TRANSITION_NEEDS[input.to];
    if (need === 'tracking' && (!input.tracking?.carrier.trim() || !input.tracking?.awb.trim())) throw new ServiceError('Carrier and tracking number are required to ship an order.', 'INVALID');
    if (need === 'reason' && !(input.note ?? '').trim()) throw new ServiceError(`A reason is required to mark an order as ${titleCase(input.to)}.`, 'INVALID');

    const now = nowIso();
    const from: OrderStatus = o.status;
    o.status = input.to;
    o.timeline.push({ status: input.to, at: now, note: input.note?.trim() || `Moved from ${titleCase(from)} by ${actor.user.name}` });
    if (input.to === 'PAID') o.paymentStatus = 'PAID';
    if (input.to === 'SHIPPED' && input.tracking) o.tracking = input.tracking;
    if (input.to === 'DELIVERED' && o.paymentMethod === 'COD') o.paymentStatus = 'PAID'; // cash collected
    if (input.to === 'CANCELLED') o.paymentStatus = o.paymentStatus === 'PAID' ? 'REFUNDED' : 'FAILED';
    if (input.to === 'REFUNDED') o.paymentStatus = 'REFUNDED';

    // Side effects go through the ledgers (append-only), never by editing balances.
    if ((input.to === 'CANCELLED' || input.to === 'REFUNDED') && o.pricing.walletApplied > 0 && d.features.wallet) {
      const bal = customerBalance(d, o.customer.id) + o.pricing.walletApplied;
      d.wallet.push({
        id: uid('wtx'), tenantId: d.tenantId, customerId: o.customer.id, customerName: o.customer.name, type: 'REFUND', direction: 'CREDIT',
        amount: o.pricing.walletApplied, balanceAfter: bal, reference: o.number, reason: input.note?.trim(), actorName: actor.user.name, createdAt: now,
      });
    }
    if (input.to === 'CANCELLED' && d.inventory.some((e) => e.reason === 'SALE' && e.note === o.number)) {
      for (const it of o.items) {
        const p = d.products.find((x) => x.id === it.productId);
        if (!p) continue;
        d.inventory.push({ id: uid('inv'), tenantId: d.tenantId, productId: p.id, productName: p.name, sku: p.sku, delta: it.quantity, before: p.stock, after: p.stock + it.quantity, reason: 'RETURN', note: `${o.number} cancelled`, actorName: actor.user.name, createdAt: now });
        p.stock += it.quantity;
        if (p.variants.length) p.variants[0].stock += it.quantity;
      }
    }
    saveAdmin();
    return delay(o, 600);
  },
  async addNote(id, text) {
    const { actor, data: d } = ctx('orders.manage', 'orders');
    const o = d.orders.find((x) => x.id === id);
    if (!o) throw notFound('Order');
    if (!text.trim()) throw new ServiceError('Write a note first', 'INVALID');
    const note = { id: uid('note'), text: text.trim(), authorName: actor.user.name, createdAt: nowIso() };
    o.notes.push(note);
    saveAdmin();
    return delay(note, 300);
  },
  async counts() {
    const { data: d } = ctx('orders.read', 'orders');
    const c = { all: d.orders.length } as Record<OrderStatus | 'all', number>;
    ORDER_STATUSES.forEach((s) => (c[s] = d.orders.filter((o) => o.status === s).length));
    return delay(c, 200);
  },
};

export const adminCustomers: AdminCustomerService = {
  async list(q) {
    const { data: d } = ctx('customers.read');
    const anchor = anchorTime(d);
    const rows = hydrateCustomers(d)
      .filter((c) => {
        if (q.status && q.status !== 'all' && c.status !== q.status) return false;
        if (!matches(q.q, c.name, c.email, c.phone, c.city)) return false;
        switch (q.segment) {
          case 'new': return anchor - Date.parse(c.joinedAt) < 30 * DAY_MS;
          case 'repeat': return c.ordersCount >= 2;
          case 'wallet': return c.walletBalance > 0;
          case 'inactive': return anchor - Date.parse(c.lastActiveAt) > 45 * DAY_MS;
          default: return true;
        }
      })
      .sort((a, b) => q.sort === 'spent' ? b.totalSpent - a.totalSpent : q.sort === 'orders' ? b.ordersCount - a.ordersCount : q.sort === 'name' ? a.name.localeCompare(b.name) : b.lastActiveAt.localeCompare(a.lastActiveAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async get(id): Promise<CustomerProfile> {
    const { data: d } = ctx('customers.read');
    const customer = hydrateCustomers(d).find((c) => c.id === id);
    if (!customer) throw notFound('Customer');
    const orders = d.orders.filter((o) => o.customer.id === id).sort((a, b) => b.placedAt.localeCompare(a.placedAt));
    const wallet = d.features.wallet ? d.wallet.filter((w) => w.customerId === id).sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : [];
    const loyalty = d.features.loyalty ? d.loyalty.filter((l) => l.customerId === id).sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : [];
    const used = new Map(orders.filter((o) => o.couponCode).map((o) => [o.couponCode!, o.placedAt]));
    const now = Date.now();
    const coupons = d.features.coupons
      ? d.coupons.filter((c) => c.enabled || used.has(c.code)).map((c) => ({
        code: c.code, title: c.title, usedAt: used.get(c.code),
        status: (used.has(c.code) && c.perCustomerLimit <= 1 ? 'USED' : Date.parse(c.expiresAt) < now ? 'EXPIRED' : 'AVAILABLE') as 'USED' | 'EXPIRED' | 'AVAILABLE',
      }))
      : [];
    const activity: ActivityEvent[] = [
      ...orders.map((o) => ({ id: `a_${o.id}`, type: 'ORDER' as const, text: `Placed order ${o.number} (${titleCase(o.status)})`, at: o.placedAt })),
      ...wallet.map((w) => ({ id: `a_${w.id}`, type: 'WALLET' as const, text: `${titleCase(w.type)}: ${w.direction === 'CREDIT' ? '+' : '−'}₹${w.amount}`, at: w.createdAt })),
      ...loyalty.map((l) => ({ id: `a_${l.id}`, type: 'LOYALTY' as const, text: `${l.description} (${l.points > 0 ? '+' : ''}${l.points} pts)`, at: l.createdAt })),
      { id: `a_join_${id}`, type: 'ACCOUNT' as const, text: 'Created an account', at: customer.joinedAt },
    ].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 40);
    return delay({ customer, orders, addresses: d.addresses[id] ?? [], coupons, wallet, loyalty, activity });
  },
  async setStatus(id, status) {
    const { data: d } = ctx('store.manage');
    const c = d.customers.find((x) => x.id === id);
    if (!c) throw notFound('Customer');
    c.status = status;
    saveAdmin();
    return delay(hydrateCustomers(d).find((x) => x.id === id)!, 400);
  },
};
